import { useState } from 'react';

const MAX_PROPERTIES = 3;

// Common labels a committee is likely to reach for, offered as quick-fill chips.
// The label is free-text — these are only suggestions.
const SUGGESTED_KEYS = ['Start', 'VHF', 'Day', 'Area', 'Flag'];

/**
 * Per-course-card "lozenges" editor: up to 3 generic {key, value} properties
 * the app renders as lozenges (e.g. {Start: Sparetime}, {VHF: 71}). Replaces the
 * old bespoke per-field lozenge logic (DAY/START/VHF) with a data-driven model
 * the committee controls per card (ra-hi9d).
 *
 * Controlled: `properties` is the current array; every edit emits the full new
 * array via `onChange`.
 */
export default function CardPropertiesEditor({ properties = [], onChange }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const atMax = properties.length >= MAX_PROPERTIES;

  const addProperty = (key = '') => {
    if (atMax) return;
    onChange([...properties, { key, value: '' }]);
    setShowSuggestions(false);
  };

  const updateProperty = (idx, field, value) => {
    onChange(properties.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const removeProperty = (idx) => {
    onChange(properties.filter((_, i) => i !== idx));
  };

  const usedKeys = new Set(
    properties.map((p) => (p.key || '').trim().toLowerCase()),
  );
  const preview = properties.filter((p) => (p.key || '').trim() || (p.value || '').trim());

  return (
    <div className="cc-props">
      <span className="form-label">Card lozenges</span>
      <p className="section-hint">
        Up to {MAX_PROPERTIES} short labels shown on the course card in the app
        (e.g. <code>Start: Sparetime</code>, <code>VHF: 71</code>). Leave empty to
        show none.
      </p>

      {properties.length > 0 && (
        <div className="cc-props-rows">
          {properties.map((p, i) => (
            <div key={i} className="cc-props-row">
              <input
                type="text"
                className="inline-input cc-props-key"
                value={p.key || ''}
                placeholder="Label (e.g. Start)"
                aria-label={`Property ${i + 1} label`}
                onChange={(e) => updateProperty(i, 'key', e.target.value)}
              />
              <span className="cc-props-sep">:</span>
              <input
                type="text"
                className="inline-input cc-props-value"
                value={p.value || ''}
                placeholder="Value (e.g. Sparetime)"
                aria-label={`Property ${i + 1} value`}
                onChange={(e) => updateProperty(i, 'value', e.target.value)}
              />
              <button
                type="button"
                className="btn btn-danger btn-sm"
                aria-label={`Remove property ${i + 1}`}
                onClick={() => removeProperty(i)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {!atMax && (
        <div className="cc-props-add">
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => addProperty()}
          >
            + Add lozenge
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setShowSuggestions((s) => !s)}
          >
            Suggestions
          </button>
          {showSuggestions && (
            <div className="cc-props-suggestions">
              {SUGGESTED_KEYS.filter((k) => !usedKeys.has(k.toLowerCase())).map(
                (k) => (
                  <button
                    key={k}
                    type="button"
                    className="cc-lozenge"
                    onClick={() => addProperty(k)}
                  >
                    + {k}
                  </button>
                ),
              )}
            </div>
          )}
        </div>
      )}
      {atMax && (
        <p className="section-hint cc-props-max">
          Maximum {MAX_PROPERTIES} lozenges reached.
        </p>
      )}

      {/* Live preview — what the sailor sees on the course card */}
      {preview.length > 0 && (
        <div className="cc-props-preview" aria-label="App preview">
          <span className="cc-props-preview-label">App preview</span>
          <div className="cc-props-preview-row">
            {preview.map((p, i) => (
              <div key={i} className="cc-props-lozenge">
                <span className="cc-props-lozenge-value">
                  {(p.value || '').trim() || '—'}
                </span>
                <span className="cc-props-lozenge-key">
                  {((p.key || '').trim() || 'LABEL').toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
